import { useState, useEffect } from "react"
import { BookOpen, X, Save, Loader2, Clock, Languages } from "lucide-react"
import { Button } from "../ui/button"

export default function ModuleForm({
  module = null, // null pour création, objet pour édition
  onSave,
  onCancel,
  modulesCount = 0,
  onShowTranslations
}) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    order: 1,
    duration: 0
  })
  const [errors, setErrors] = useState({})
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (module) {
      setFormData({
        title: module.title || '',
        description: module.description || '',
        order: module.order || 1,
        duration: module.duration || 0
      })
    } else {
      setFormData({
        title: '',
        description: '',
        order: modulesCount + 1,
        duration: 0
      })
    }
  }, [module, modulesCount])

  const handleChange = (e) => {
    const { name, value, type } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : value
    }))
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }))
    }
  }

  const validate = () => {
    const newErrors = {}
    if (!formData.title.trim()) {
      newErrors.title = 'Le titre est obligatoire'
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validate()) return

    setSaving(true)
    try {
      await onSave(formData)
    } catch (error) {
      console.error('Error saving module:', error)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[#141835] rounded-lg">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {module ? 'Modifier le module' : 'Nouveau Module'}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {module ? 'Modifiez les informations du module' : 'Créez un nouveau module pour votre cours'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {module && onShowTranslations && (
              <Button
                variant="outline"
                size="sm"
                onClick={onShowTranslations}
                className="h-8"
                title="Gérer les traductions du module"
              >
                <Languages className="w-4 h-4 mr-2" />
                Traductions
              </Button>
            )}
            {onCancel && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onCancel}
                className="h-8"
              >
                <X className="w-4 h-4 mr-2" />
                Annuler
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <form onSubmit={handleSubmit} className="max-w-2xl mx-auto">
          <div className="bg-white rounded-lg shadow-sm p-6 space-y-6">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Titre <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835] ${
                  errors.title ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Ex: Introduction à React"
                required
              />
              {errors.title && (
                <p className="mt-1 text-sm text-red-600">{errors.title}</p>
              )}
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                placeholder="Description du module..."
              />
            </div>

            {/* Order and Duration */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ordre
                </label>
                <input
                  type="number"
                  name="order"
                  value={formData.order}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                  min="1"
                  placeholder="1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  Durée (minutes)
                </label>
                <input
                  type="number"
                  name="duration"
                  value={formData.duration}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                  min="0"
                  placeholder="0"
                />
              </div>
            </div>
          </div>
        </form>
      </div>

      {/* Footer */}
      <div className="bg-white border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={saving}
          >
            Annuler
          </Button>
        )}
        <Button
          type="submit"
          onClick={handleSubmit}
          disabled={saving}
          className="bg-[#141835] hover:bg-[#1a1f4a] text-white"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              {module ? 'Modification...' : 'Création...'}
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              {module ? 'Modifier' : 'Créer le module'}
            </>
          )}
        </Button>
      </div>
    </div>
  )
}

